import type { inferRouterOutputs } from "@trpc/server";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import type { AppRouter } from "@runcheetah/api/router";
import {
  WORKSPACE_BILLING_CHANGEABLE_STATUSES,
  WORKSPACE_BILLING_RECHECKOUT_STATUSES,
} from "@runcheetah/shared";
import { Button, cn } from "@runcheetah/ui";

import { trpc } from "@/lib/trpc";

import { BillingModalShell } from "./billing-modal-shell";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type BillingOverview = RouterOutputs["brand"]["getBillingOverview"];
type BillingPlan = RouterOutputs["billing"]["listPlans"][number];
type CreditPack = RouterOutputs["billing"]["listCreditPacks"][number];
type BillingCycle = "monthly" | "yearly";
type BillingAccount = NonNullable<BillingOverview["account"]>;

const getPlanPrice = (plan: BillingPlan, billingCycle: BillingCycle): number | null =>
  billingCycle === "monthly" ? plan.monthlyPriceCents : plan.yearlyPriceCents;

const formatMoney = (value: number | null, locale: string): string | null => {
  if (value === null) {
    return null;
  }

  return new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(value / 100);
};

const formatNumber = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale).format(value);

const sortPlansByCyclePrice = (plans: BillingPlan[], billingCycle: BillingCycle) =>
  plans.toSorted((left, right) => {
    const leftPrice = getPlanPrice(left, billingCycle);
    const rightPrice = getPlanPrice(right, billingCycle);

    if (leftPrice === null && rightPrice !== null) return 1;
    if (leftPrice !== null && rightPrice === null) return -1;
    if (leftPrice !== null && rightPrice !== null && leftPrice !== rightPrice) {
      return leftPrice - rightPrice;
    }

    return (
      (left.monthlyCreditAllowance ?? 0) - (right.monthlyCreditAllowance ?? 0) ||
      (left.brandLimit ?? 0) - (right.brandLimit ?? 0) ||
      left.name.localeCompare(right.name)
    );
  });

export const hasActiveStripeSubscription = (account: BillingAccount | null): boolean =>
  Boolean(
    account?.hasStripeSubscription &&
    !WORKSPACE_BILLING_RECHECKOUT_STATUSES.includes(account.status),
  );

export const isCurrentBillingPlan = ({
  account,
  planId,
  availablePlanId,
  selectedCycle,
}: {
  account: BillingAccount | null;
  planId: string | null | undefined;
  availablePlanId: string;
  selectedCycle: BillingCycle;
}): boolean =>
  Boolean(
    hasActiveStripeSubscription(account) &&
    planId === availablePlanId &&
    account?.billingCycle === selectedCycle,
  );

export function AssetCreationUpgradeModal({
  brandId,
  overview,
  plans,
  plansPending,
  creditPacks,
  creditPacksPending,
  returnPath,
  open,
  onOpenChange,
  preferPlans = false,
}: {
  brandId: string | undefined;
  overview: BillingOverview | undefined;
  plans: BillingPlan[];
  plansPending: boolean;
  creditPacks: CreditPack[];
  creditPacksPending: boolean;
  returnPath?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preferPlans?: boolean;
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const account = overview?.account ?? null;
  const plan = overview?.plan ?? null;
  const [selectedCycle, setSelectedCycle] = useState<BillingCycle>(
    account?.billingCycle ?? "monthly",
  );
  const createCheckoutSession = trpc.billing.createCheckoutSession.useMutation({
    onSuccess: (data) => window.location.assign(data.url),
  });
  const createCreditPackCheckoutSession = trpc.billing.createCreditPackCheckoutSession.useMutation({
    onSuccess: (data) => window.location.assign(data.url),
  });
  const changeSubscriptionPlan = trpc.billing.changeSubscriptionPlan.useMutation({
    onSuccess: async () => {
      if (brandId) {
        await utils.brand.getBillingOverview.invalidate({ brandId });
      }
      onOpenChange(false);
    },
  });
  const createPortalSession = trpc.billing.createPortalSession.useMutation({
    onSuccess: (data) => window.location.assign(data.url),
  });

  if (!open) {
    return null;
  }

  const canChangePlan = Boolean(
    account &&
    account.hasStripeSubscription &&
    WORKSPACE_BILLING_CHANGEABLE_STATUSES.includes(account.status),
  );
  const canStartCheckout = Boolean(
    !account ||
    !account.hasStripeSubscription ||
    WORKSPACE_BILLING_RECHECKOUT_STATUSES.includes(account.status),
  );
  const canBuyCreditPacks = Boolean(
    !preferPlans &&
    account?.hasStripeSubscription &&
    WORKSPACE_BILLING_CHANGEABLE_STATUSES.includes(account.status),
  );
  const shouldManageBillingBeforeChange = Boolean(account && !canChangePlan && !canStartCheckout);
  const sortedPlans = sortPlansByCyclePrice(plans, selectedCycle);
  const pending =
    createCheckoutSession.isPending ||
    createCreditPackCheckoutSession.isPending ||
    changeSubscriptionPlan.isPending ||
    createPortalSession.isPending;
  const actionError =
    createCheckoutSession.error?.message ??
    createCreditPackCheckoutSession.error?.message ??
    changeSubscriptionPlan.error?.message ??
    createPortalSession.error?.message ??
    null;

  const handlePlanAction = (availablePlan: BillingPlan) => {
    if (!brandId) return;

    if (shouldManageBillingBeforeChange) {
      createPortalSession.mutate({ brandId, returnPath });
      return;
    }

    const input = { brandId, planId: availablePlan.id, billingCycle: selectedCycle, returnPath };
    if (canChangePlan) {
      changeSubscriptionPlan.mutate(input);
      return;
    }

    createCheckoutSession.mutate(input);
  };

  const handleCreditPackAction = (creditPackId: string) => {
    if (!brandId) return;

    createCreditPackCheckoutSession.mutate({ brandId, creditPackId, returnPath });
  };

  return (
    <BillingModalShell
      title={intl.formatMessage(
        canBuyCreditPacks
          ? {
              id: "assetCreationUpgradeModal.buyCreditsTitle",
              defaultMessage: "Add extra runs to keep creating",
            }
          : {
              id: "assetCreationUpgradeModal.title",
              defaultMessage: "Upgrade to keep creating",
            },
      )}
      description={intl.formatMessage(
        canBuyCreditPacks
          ? {
              id: "assetCreationUpgradeModal.buyCreditsDescription",
              defaultMessage:
                "You've used your plan runs. Buy extra runs now, then your plan allowance refills on your next billing period.",
            }
          : {
              id: "assetCreationUpgradeModal.description",
              defaultMessage:
                "Choose a plan with enough runs to generate campaigns, posts, and image updates.",
            },
      )}
      onClose={() => !pending && onOpenChange(false)}
      widthClassName="max-w-[680px]"
    >
      {canBuyCreditPacks ? null : (
        <div className="flex justify-center">
          <div className="inline-flex rounded-full border border-[rgba(13,31,45,.08)] bg-[rgba(13,31,45,.035)] p-1">
            {(["monthly", "yearly"] satisfies BillingCycle[]).map((cycle) => (
              <Button
                key={cycle}
                type="button"
                size="sm"
                variant={selectedCycle === cycle ? "default" : "ghost"}
                className={cn(
                  "rounded-full px-5 text-xs font-extrabold tracking-[-0.01em]",
                  selectedCycle === cycle
                    ? "bg-[#0D1F2D] text-[#C8FF00] hover:bg-[#0D1F2D]/95"
                    : "text-[#0D1F2D]/65 hover:bg-white/70 hover:text-[#0D1F2D]",
                )}
                onClick={() => setSelectedCycle(cycle)}
              >
                {cycle === "monthly" ? (
                  <FormattedMessage
                    id="brandSettings.billing.monthlyCycle"
                    defaultMessage="Monthly"
                  />
                ) : (
                  <FormattedMessage
                    id="brandSettings.billing.yearlyCycle"
                    defaultMessage="Yearly"
                  />
                )}
              </Button>
            ))}
          </div>
        </div>
      )}

      {actionError ? (
        <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {actionError}
        </div>
      ) : null}

      {canBuyCreditPacks ? (
        <div className="max-h-[52vh] space-y-2 overflow-y-auto pr-1">
          {creditPacksPending ? (
            <div className="flex items-center justify-center gap-2 rounded-2xl border border-border/70 px-4 py-8 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              <FormattedMessage
                id="assetCreationUpgradeModal.loadingCreditPacks"
                defaultMessage="Loading run packs..."
              />
            </div>
          ) : creditPacks.length === 0 ? (
            <div className="rounded-2xl border border-border/70 px-4 py-8 text-center text-sm text-muted-foreground">
              <FormattedMessage
                id="brandSettings.billing.noCreditPacks"
                defaultMessage="Run packs are not available yet."
              />
            </div>
          ) : null}
          {creditPacks.map((pack) => (
            <div
              key={pack.id}
              className="flex flex-col gap-3 rounded-[14px] border border-[rgba(13,31,45,.08)] bg-white p-4 text-[#0D1F2D] sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <div className="text-xs font-extrabold uppercase tracking-[0.08em] text-[#0D1F2D]/45">
                  <FormattedMessage
                    id="brandSettings.billing.runPackName"
                    defaultMessage="{count} run pack"
                    values={{ count: formatNumber(pack.credits, intl.locale) }}
                  />
                </div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-[28px] font-extrabold tracking-[-0.03em]">
                    {formatNumber(pack.credits, intl.locale)}
                  </span>
                  <span className="text-xs font-semibold text-[#0D1F2D]/55">
                    <FormattedMessage id="brandSettings.billing.runsUnit" defaultMessage="runs" />
                  </span>
                </div>
                <div className="mt-1 text-xs leading-5 text-[#0D1F2D]/55">
                  <FormattedMessage
                    id="brandSettings.billing.creditPackExpiry"
                    defaultMessage="Valid for {days} days"
                    values={{ days: pack.expiresAfterDays }}
                  />
                </div>
              </div>
              <div className="flex shrink-0 items-center justify-between gap-3 sm:justify-end">
                <div className="text-sm font-extrabold">
                  {formatMoney(pack.priceCents, intl.locale)}
                </div>
                <Button
                  type="button"
                  disabled={pending}
                  onClick={() => handleCreditPackAction(pack.id)}
                  className="rounded-[10px] bg-[#0D1F2D] px-[14px] py-2.5 text-[13px] font-extrabold tracking-[-0.01em] text-[#C8FF00] hover:bg-[#0D1F2D]/95"
                >
                  {createCreditPackCheckoutSession.isPending ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  <FormattedMessage
                    id="brandSettings.billing.buyCredits"
                    defaultMessage="Buy extra runs"
                  />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-[14px] md:grid-cols-2">
          {plansPending ? (
            <div className="col-span-full flex items-center justify-center gap-2 rounded-2xl border border-border/70 px-4 py-8 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              <FormattedMessage
                id="assetCreationUpgradeModal.loadingPlans"
                defaultMessage="Loading plans..."
              />
            </div>
          ) : sortedPlans.length === 0 ? (
            <div className="col-span-full rounded-2xl border border-border/70 px-4 py-8 text-center text-sm text-muted-foreground">
              <FormattedMessage
                id="assetCreationUpgradeModal.noPlans"
                defaultMessage="No upgrade plans are available right now. Please try again later."
              />
            </div>
          ) : null}
          {sortedPlans.map((availablePlan) => {
            const price = getPlanPrice(availablePlan, selectedCycle);
            const isCurrentPlan = isCurrentBillingPlan({
              account,
              planId: plan?.id,
              availablePlanId: availablePlan.id,
              selectedCycle,
            });
            const featured = availablePlan.isMostPopular;

            return (
              <div
                key={availablePlan.id}
                className={cn(
                  "relative flex min-h-[300px] flex-col gap-[14px] rounded-[14px] border p-5",
                  featured
                    ? "border-[#0D1F2D] bg-[#0D1F2D] text-white"
                    : "border-[rgba(13,31,45,.08)] bg-white text-[#0D1F2D]",
                )}
              >
                {featured ? (
                  <span className="absolute -top-[10px] right-4 rounded-full bg-[#C8FF00] px-[9px] py-[3px] text-[10px] font-extrabold uppercase tracking-[0.05em] text-[#0D1F2D]">
                    <FormattedMessage
                      id="brandSettings.billing.mostPopular"
                      defaultMessage="Most popular"
                    />
                  </span>
                ) : null}
                <div className="text-sm font-extrabold tracking-[-0.01em]">
                  {availablePlan.name}
                </div>
                <div>
                  <span className="text-[28px] font-extrabold tracking-[-0.02em]">
                    {formatMoney(price, intl.locale) ??
                      intl.formatMessage({
                        id: "brandSettings.billing.priceUnavailable",
                        defaultMessage: "N/A",
                      })}
                  </span>
                  {price !== null ? (
                    <span
                      className={cn(
                        "ml-1 text-[11px]",
                        featured ? "text-white/65" : "text-[rgba(13,31,45,.6)]",
                      )}
                    >
                      {selectedCycle === "yearly" ? (
                        <FormattedMessage
                          id="brandSettings.billing.planPerYear"
                          defaultMessage="/yr"
                        />
                      ) : (
                        <FormattedMessage
                          id="brandSettings.billing.planPerMonth"
                          defaultMessage="/mo"
                        />
                      )}
                    </span>
                  ) : null}
                </div>
                <div className="border-t border-current/10 pt-3 text-xs leading-5">
                  <FormattedMessage
                    id="brandSettings.billing.planAllowanceSummary"
                    defaultMessage="{brandLimit} brands · {credits} runs/month"
                    values={{
                      brandLimit: availablePlan.brandLimit ?? 0,
                      credits: availablePlan.monthlyCreditAllowance ?? 0,
                    }}
                  />
                </div>
                <Button
                  type="button"
                  disabled={price === null || isCurrentPlan || pending}
                  onClick={() => handlePlanAction(availablePlan)}
                  className={cn(
                    "mt-auto rounded-[10px] px-[14px] py-2.5 text-[13px] font-extrabold tracking-[-0.01em]",
                    featured
                      ? "bg-[#C8FF00] text-[#0D1F2D] hover:bg-[#C8FF00]/90"
                      : "bg-[#0D1F2D] text-[#C8FF00] hover:bg-[#0D1F2D]/95",
                  )}
                >
                  {pending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
                  {isCurrentPlan ? (
                    <FormattedMessage
                      id="brandSettings.billing.planCurrent"
                      defaultMessage="Current plan"
                    />
                  ) : canChangePlan ? (
                    <FormattedMessage
                      id="brandSettings.billing.changePlan"
                      defaultMessage="Change plan"
                    />
                  ) : (
                    <FormattedMessage
                      id="brandSettings.billing.subscribe"
                      defaultMessage="Subscribe"
                    />
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </BillingModalShell>
  );
}
